import { PROD_ENT, SESSION, REGISTER, OFFLINE_ID, QUEUE, STATE_SLICE } from '../../../constants';
import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { RootState } from 'types';
import { hydrate, IPersist, persist, restoreState, updateState } from 'core/actions/product/state';
import {
  actionRequest,
  attemptRequest,
  dialogueRequest,
  homeRequest,
  suspenseRequest,
  tokenRequest,
} from 'core/services/product';
import { clearCache, clearError, logout, restartToken, updateToken, updateTokenState } from 'core/slices/session';
import {
  authRequest,
  authGoogleRequest,
  validationRequest,
  passwordRequest,
  systemSigninRequest,
  newSigninRequest,
} from 'core/services/authentication';
import { PayloadAction } from '@reduxjs/toolkit';
import { clearCookies } from '../auth/auth';
import { EncryptStorage } from 'encrypt-storage';
import {
  decrementIndex,
  goToIndex,
  incrementIndex,
  preview,
  registerAttempt,
  updateVerification,
} from 'core/slices/register';
import { generateOfflineId } from 'core/slices/offline';
import { EntityTypes } from 'constants/entities';
import { queueItem } from 'core/slices/queue';
import { env } from 'utils/env';

export const encryptStorage = new EncryptStorage(env.SECRET, {});

function* init() {
  if (!encryptStorage || !encryptStorage.getItem) return;

  try {
    // The entity cache is no longer persisted — see updateProdEnt's removal below.
    // Drop any blob written by an older build so it stops taking up storage.
    yield call([encryptStorage, encryptStorage.removeItem], PROD_ENT);

    const session = yield call([encryptStorage, encryptStorage.getItem], SESSION);
    const register = yield call([encryptStorage, encryptStorage.getItem], REGISTER);
    const queue = yield call([encryptStorage, encryptStorage.getItem], QUEUE);
    const stateSlice = yield call([encryptStorage, encryptStorage.getItem], STATE_SLICE);
    const offlineId = yield call([encryptStorage, encryptStorage.getItem], OFFLINE_ID);

    yield put(
      yield call(hydrate, {
        prodEnt: null,
        session: session !== undefined ? session : null,
        register: register !== undefined ? register : null,
        queue: queue !== undefined ? queue : null,
        stateSlice: stateSlice !== undefined ? stateSlice : null,
        offlineId,
      }),
    );
  } catch (e) {}
}

function* updateSessionSaga() {
  const session = yield select((state: RootState) => state.session);
  try {
    yield call([encryptStorage, encryptStorage.setItem], SESSION, session);
  } catch (e) {}
}

function* updateRegisterSaga() {
  const register = yield select((state: RootState) => state.register);
  const queue = yield select((state: RootState) => state.queue);

  try {
    yield call([encryptStorage, encryptStorage.setItem], REGISTER, register);
    yield call([encryptStorage, encryptStorage.setItem], QUEUE, queue);
  } catch (e) {}
}

const PLAYER_STATE_FIELDS = [
  EntityTypes.COURSE_PROGRESS,
  EntityTypes.SESSION_ATTEMPT,
  EntityTypes.MODULE,
  EntityTypes.ACTION,
];

function* updateStateSliceSaga() {
  const stateSlice = yield select((state: RootState) =>
    PLAYER_STATE_FIELDS.reduce((acc, field) => {
      acc[field] = state.state[field];
      return acc;
    }, {} as Record<string, number>),
  );
  try {
    yield call([encryptStorage, encryptStorage.setItem], STATE_SLICE, stateSlice);
  } catch (e) {}
}

function* persistActionSaga(action: PayloadAction<Partial<IPersist>>) {
  if (!encryptStorage || !encryptStorage.setItem) return;

  if (action.payload.session) yield updateSessionSaga();
  if (action.payload.register) yield updateRegisterSaga();
}

function* clearCacheSaga() {
  if (!window.localStorage || !window.localStorage.clear) return;

  try {
    yield call([window.localStorage, window.localStorage.clear]);
    yield clearCookies();
  } catch (e) {}
}

function* offlineIdSaga() {
  if (!encryptStorage || !encryptStorage.setItem) return;

  const offlineId = yield select((state: RootState) => state.core.offline.id);
  yield call([encryptStorage, encryptStorage.setItem], OFFLINE_ID, offlineId);
}

function* watchClearCache() {
  yield takeLatest(clearCache, clearCacheSaga);
}

function* persistAction() {
  yield takeLatest(persist, persistActionSaga);
}

function* updateSession() {
  yield takeLatest(
    [
      homeRequest.fulfilled,
      tokenRequest.fulfilled,
      systemSigninRequest.fulfilled,
      updateToken,
      updateTokenState,
      authRequest.fulfilled,
      authGoogleRequest.fulfilled,
      validationRequest.fulfilled,
      passwordRequest.fulfilled,
      newSigninRequest.fulfilled,
      clearError,
      logout,
    ],
    updateSessionSaga,
  );
}

function* watchGenerateOfflineId() {
  yield takeLatest([generateOfflineId, registerAttempt, preview], offlineIdSaga);
}

function* updateRegister() {
  yield takeEvery(
    [
      updateVerification,
      registerAttempt,
      incrementIndex,
      decrementIndex,
      goToIndex,
      tokenRequest.fulfilled,
      actionRequest.fulfilled,
      homeRequest.fulfilled,
      attemptRequest.fulfilled,
      dialogueRequest.fulfilled,
      restartToken,
      queueItem,
      suspenseRequest.pending,
    ],
    updateRegisterSaga,
  );
}

function* watchStateSlice() {
  yield takeLatest([restoreState, updateState, incrementIndex, decrementIndex, goToIndex], updateStateSliceSaga);
}

export default function* persistSaga() {
  yield all([
    fork(init),
    // The entity store is deliberately not persisted: modules refetch on refresh, so
    // the cache bought little and served stale content. Only the small player-position
    // slices below are kept.
    fork(updateSession),
    fork(updateRegister),
    fork(persistAction),
    fork(watchClearCache),
    fork(watchGenerateOfflineId),
    fork(watchStateSlice),
  ]);
}
